import { motion } from "framer-motion";
import { useState } from "react";
import { Wrench, Leaf, Users, FileCheck2, ShieldCheck, Recycle } from "lucide-react";
import { Container, Reveal, SectionHead, BtnLink, TextLink, Eyebrow, CheckList } from "../components/site/Primitives";
import { IMG, STATS, APART, PRODUCTS } from "../data/site";

const Hero = () => (
  <section data-testid="home-hero" className="relative isolate overflow-hidden bg-navy">
    <motion.img src={IMG("home_hero-factory-bg")} alt="MU Plast SACMI closure production lines"
      initial={{ scale: 1.08 }} animate={{ scale: 1 }} transition={{ duration: 2.2, ease: "easeOut" }}
      className="absolute inset-0 -z-10 h-full w-full object-cover" />
    <div className="absolute inset-0 -z-10 bg-gradient-to-r from-navy/90 via-navy/70 to-navy/20" />
    <Container className="pt-24 pb-40 sm:pt-32 sm:pb-48 lg:pt-36 lg:pb-52">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2 }} className="max-w-3xl">
        <Eyebrow light>ISO 9001:2015 & FSSC 22000 Certified Manufacturing</Eyebrow>
        <h1 className="font-display mt-7 text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.1] text-white">
          Your trusted partner for innovation, quality &amp; reliability.
        </h1>
        <p className="mt-8 max-w-2xl border-l-[3px] border-[#2EE6D6] pl-5 text-base md:text-lg leading-relaxed text-white/85">
          SACMI powered plastic closures and PET preforms with advanced hot runner technology. ISO 9001:2015 & FSSC 22000 certified manufacturing — consistent quality at scale — trusted by leading brands.
        </p>
        <div className="mt-10 flex flex-wrap gap-3">
          <BtnLink to="/products" variant="light" testId="hero-explore-products-btn">Explore products</BtnLink>
          <BtnLink to="/contact" variant="ghostLight" testId="hero-request-quote-btn">Request a quote</BtnLink>
        </div>
      </motion.div>
    </Container>
  </section>
);

const Stats = () => (
  <Container className="relative z-10 -mt-20">
    <motion.ul initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.5 }}
      data-testid="home-stats" className="grid grid-cols-2 lg:grid-cols-4 overflow-hidden rounded-3xl bg-white text-ink border border-slate-200 shadow-[0_30px_60px_-30px_rgba(39,50,114,0.35)]">
      {STATS.map((s, i) => (
        <li key={s.label} data-testid={`home-stat-${i}`} className={`p-6 sm:p-8 lg:p-10 border-slate-200 ${i % 2 ? "" : "border-r"} ${i < 2 ? "border-b lg:border-b-0" : ""} lg:border-r lg:last:border-r-0`}>
          <p className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-navy">{s.value}</p>
          <p className="mt-2 text-sm text-slate-500">{s.label}</p>
        </li>
      ))}
    </motion.ul>
  </Container>
);

const AboutPreview = () => (
  <section className="py-24 lg:py-32">
    <Container className="grid gap-14 lg:grid-cols-2 lg:gap-20 items-center">
      <div>
        <SectionHead eyebrow="About MU Plast" title="Committed to quality, built on trust" />
        <Reveal delay={0.1} className="mt-6 space-y-5 text-slate-600 leading-relaxed">
          <p>We are among the trusted manufacturers of PET preforms and HDPE caps, delivering high-performance packaging solutions for beverage and FMCG industries. Our advanced manufacturing facilities and strict quality control ensure consistency, durability, and precision in every product.</p>
          <p>Driven by innovation and a customer-first approach, we continuously refine our processes to meet evolving industry demands and global packaging standards — aiming to become a dependable extension of our clients' supply chain.</p>
          <div className="pt-3"><TextLink to="/about" testId="home-about-link">Read more about us</TextLink></div>
        </Reveal>
      </div>
      <Reveal delay={0.15} className="relative">
        <div className="img-zoom overflow-hidden aspect-[4/3]"><img src={IMG("home_about-preview-factory")} alt="Manufacturing facility" className="h-full w-full object-cover" /></div>
        <div className="absolute -bottom-6 -left-6 hidden sm:block rounded-2xl bg-white shadow-xl px-7 py-6 text-navy">
          <p className="font-display text-3xl font-semibold">Est. 2022</p>
          <p className="text-xs uppercase tracking-[0.18em] text-slate-500 mt-1">Shrirampur, Maharashtra</p>
        </div>
      </Reveal>
    </Container>
  </section>
);

const MACHINES = [
  {
    id: "sacmi",
    brand: "SACMI · Italy",
    title: "SACMI CCM48 compression moulding",
    use: "Closures",
    body: "State-of-the-art SACMI CCM48 continuous compression moulding lines capable of producing over 1 billion closures annually, with exceptional dimensional accuracy and uniform quality.",
    points: ["Continuous rotary compression moulding", "Precise dosing and low process losses", "In-line inspection before packing"],
    imgs: [["sacmi-ccm48", "SACMI CCM48"]],
  },
  {
    id: "milacron",
    brand: "Milacron",
    title: "Milacron injection moulding",
    use: "PET preforms",
    body: "Milacron injection moulding machines with advanced hot runner technology produce premium PET preforms—engineered for strength, clarity, and reliable performance on high-speed bottling lines.",
    points: ["Hot runner technology for consistent preforms", "Alaska 27mm and 26/22 short neck programmes", "Dimensionally accurate across fifteen grammages"],
    imgs: [["milacron-q-series", "Milacron Q-Series"], ["milacron-q-series-side", "Milacron Q-Series side view"]],
  },
];

const MachineCard = ({ m, i }) => {
  const [sel, setSel] = useState(0);
  return (
    <Reveal delay={i * 0.1}>
      <article data-testid={`machine-card-${m.id}`} className="lift flex h-full flex-col border border-slate-200 bg-white">
        <div className="relative aspect-[16/10] bg-white">
          <img key={sel} src={`/img/machines/${m.imgs[sel][0]}.webp`} alt={m.imgs[sel][1]} data-testid={`machine-image-${m.id}`} className="h-full w-full object-contain p-4 animate-fade-down" />
          <span className="absolute left-5 top-5 rounded-full bg-brand-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-brand-600">{m.use}</span>
        </div>
        <div className={`flex gap-2 border-t border-slate-100 px-6 py-4 ${m.imgs.length < 2 ? "hidden" : ""}`}>
          {m.imgs.map(([f, alt], k) => (
            <button key={f} onClick={() => setSel(k)} aria-label={alt} data-testid={`machine-thumb-${m.id}-${k}`}
              className={`h-14 w-20 overflow-hidden rounded-lg border-2 bg-white transition-colors ${sel === k ? "border-brand" : "border-slate-200 hover:border-slate-300"}`}>
              <img src={`/img/machines/${f}.webp`} alt="" className="h-full w-full object-contain" />
            </button>
          ))}
        </div>
        <div className="flex flex-1 flex-col p-7 lg:p-8 pt-2 lg:pt-2">
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500">{m.brand}</p>
          <h3 className="font-display mt-2 text-2xl font-semibold text-navy">{m.title}</h3>
          <p className="mt-3 text-[15px] leading-relaxed text-slate-600">{m.body}</p>
          <div className="mt-5"><CheckList items={m.points} /></div>
        </div>
      </article>
    </Reveal>
  );
};

const Machinery = () => (
  <section className="bg-mist py-24 lg:py-32" data-testid="home-machinery">
    <Container>
      <SectionHead eyebrow="Our machinery" title="World-class machines behind every closure and preform" lead="SACMI compression lines for closures and Milacron injection systems for PET preforms — the technology that keeps our quality consistent at scale." />
      <div className="mt-14 grid gap-6 lg:grid-cols-2">
        {MACHINES.map((m, i) => <MachineCard key={m.id} m={m} i={i} />)}
      </div>
    </Container>
  </section>
);

const Apart = () => (
  <section className="py-24 lg:py-32">
    <Container>
      <SectionHead eyebrow="What sets us apart" title="Built around what your line actually needs" />
      <div className="mt-14 grid gap-6 md:grid-cols-3">
        {APART.map((a, i) => (
          <Reveal key={a.title} delay={i * 0.08}>
            <article data-testid={`apart-card-${i}`} className="lift group h-full bg-white border border-slate-200/70">
              <div className="img-zoom overflow-hidden aspect-[3/2] bg-slate-100"><img src={IMG(a.img)} alt={a.title} className="h-full w-full object-cover" /></div>
              <div className="p-7 lg:p-8">
                <p className="font-display text-sm text-brand font-semibold">0{i + 1}</p>
                <h3 className="font-display mt-2 text-xl font-semibold text-ink">{a.title}</h3>
                <p className="mt-3 text-[15px] leading-relaxed text-slate-600">{a.body}</p>
              </div>
            </article>
          </Reveal>
        ))}
      </div>
    </Container>
  </section>
);

const ProductHighlights = () => {
  const items = PRODUCTS;
  return (
    <section className="py-24 lg:py-32">
      <Container>
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8">
          <SectionHead eyebrow="Our products" title="Advanced closures & preforms for modern packaging" lead="Delivering reliable performance with a focus on quality and sustainability. Chosen by trusted brands." />
          <Reveal><BtnLink to="/products" variant="outline" testId="home-all-products-btn">All products</BtnLink></Reveal>
        </div>
        <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
          {items.map((p, i) => (
            <Reveal key={p.id} delay={i * 0.06}>
              <article data-testid={`product-card-${p.id}`} className="lift group flex h-full flex-col border border-slate-200 bg-white p-6">
                <div className="img-zoom overflow-hidden aspect-square bg-mist"><img src={IMG(p.img)} alt={p.short} className="h-full w-full object-cover" /></div>
                <h3 className="font-display mt-6 text-lg font-semibold text-ink">{p.short}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{p.blurb}</p>
                <div className="mt-5 flex-1"><CheckList items={p.highlights} /></div>
                <div className="mt-6"><TextLink to={`/products/${p.id}`} testId={`product-card-${p.id}-link`}>View details</TextLink></div>
              </article>
            </Reveal>
          ))}
        </div>
      </Container>
    </section>
  );
};

const PillarGrid = ({ items, testPrefix }) => (
  <div className="mt-14 grid gap-6 md:grid-cols-3">
    {items.map((p, i) => (
      <Reveal key={p.title} delay={i * 0.08}>
        <div data-testid={`${testPrefix}-${i}`} className="lift h-full border border-slate-200 bg-white p-8 lg:p-10">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-50 text-brand"><p.icon className="h-6 w-6" strokeWidth={1.6} /></div>
          <h3 className="font-display mt-7 text-xl font-semibold text-ink">{p.title}</h3>
          <p className="mt-3 text-[15px] leading-relaxed text-slate-600">{p.body}</p>
          <div className="mt-6"><TextLink to={p.to}>Learn more</TextLink></div>
        </div>
      </Reveal>
    ))}
  </div>
);

const OPERATE = [
  { icon: Wrench, title: "Technology", to: "/technology-quality", body: "Driven by precision-engineered molding systems and refined process control, our technology ensures uniform quality, dependable performance, and efficient production across every batch." },
  { icon: Leaf, title: "Green responsibility", to: "/sustainability", body: "By utilizing solar power, we actively lower our carbon footprint and contribute towards environmentally responsible manufacturing." },
  { icon: Users, title: "Culture", to: "/about", body: "A disciplined, safety-first culture with trained teams focused on customer success and long-term partnerships." },
];

const COMMIT = [
  { icon: FileCheck2, title: "Quality policy", to: "/about#quality-policy", body: "Documented procedures, traceability, and measurable objectives for product conformity and customer satisfaction." },
  { icon: ShieldCheck, title: "Food safety", to: "/about#food-safety", body: "Food-grade materials, hazard control, and verified release for closures and preforms on beverage lines—aligned with your audit needs." },
  { icon: Recycle, title: "Green responsibility", to: "/about#green-energy", body: "Energy efficiency, lightweighting, and recycling-compatible designs that reduce impact without compromising performance." },
];

const Range = () => (
  <section className="bg-mist py-20 lg:py-24 overflow-hidden">
    <Container>
      <SectionHead eyebrow="Colour range" title="Three closure ranges across 43 moulded colours" />
    </Container>
    <Reveal delay={0.1} className="mt-12"><img src={IMG("photos_hero_closure-range")} alt="The closure range shown in every produced colour" className="w-full max-w-none" /></Reveal>
  </section>
);

export default function Home() {
  return (
    <div data-testid="home-page">
      <Hero />
      <Stats />
      <AboutPreview />
      <Machinery />
      <Apart />
      <ProductHighlights />
      <Range />
      <section className="py-24 lg:py-32">
        <Container>
          <SectionHead eyebrow="How we operate" title="Technology, sustainability & culture" lead="The pillars that underpin reliable supply and trusted partnerships." />
          <PillarGrid items={OPERATE} testPrefix="operate-card" />
        </Container>
      </section>
      <section className="bg-mist py-24 lg:py-32">
        <Container>
          <SectionHead eyebrow="Our commitment" title="Quality, food safety & green responsibility" lead="ISO 9001:2015 & FSSC 22000 certified operations with clear policies on quality, food contact, and sustainable manufacturing—explore each pillar on our About page." />
          <PillarGrid items={COMMIT} testPrefix="commit-card" />
        </Container>
      </section>
    </div>
  );
}
